<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcWorksheetEditorFieldset',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    worksheet: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onRentalSelected = (...args) => {
    const { option, value } = args[0];
    if (option && option.client_id && option.client_name) {
      props.worksheet.client = {
        id: option.client_id,
        name: option.client_name,
      };
    }
  };

  const labelAlign = ref('right');
  const statuses = ref(null);
  const ux = ref([]);
  const b_8564662037462133_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'own',
    },
  }));
  const b_3573211234170528_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));
  const b_6639903447389385_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
    },
  }));
  const b_8017867030266407_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
      contractors_only: true,
    },
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    statuses: statuses.value,
    ux: ux.value,
    b_8564662037462133_dataSource: b_8564662037462133_dataSource.value,
    b_3573211234170528_dataSource: b_3573211234170528_dataSource.value,
    b_6639903447389385_dataSource: b_6639903447389385_dataSource.value,
    b_8017867030266407_dataSource: b_8017867030266407_dataSource.value,
  }));

  const $$load_statuses = () => {
    return $fn.value.getEnumValuesFromSchema('WorksheetStatus', {
      sort: false,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'statuses' },
      { name: 'ux' },
      { name: 'b_8564662037462133_dataSource', nodeId: '8564662037462133' },
      { name: 'b_3573211234170528_dataSource', nodeId: '3573211234170528' },
      { name: 'b_6639903447389385_dataSource', nodeId: '6639903447389385' },
      { name: 'b_8017867030266407_dataSource', nodeId: '8017867030266407' },
    ],
    loaders: { statuses: $$load_statuses },
    ux: {},
    dataRefs: {
      labelAlign,
      statuses,
      ux,
      b_8564662037462133_dataSource,
      b_3573211234170528_dataSource,
      b_6639903447389385_dataSource,
      b_8017867030266407_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return props.worksheet === 'locked';
  });

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );

  const act_3573211234170528_script_vp4a3t6u6_value_callable = async (
    $event
  ) => {
    return onRentalSelected($event.data);
  };
  const act_3573211234170528_script_vp4a3t6u6_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3573211234170528_script_vp4a3t6u6_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_s7z0xd6w7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3twt2weyk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_s7z0xd6w7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3twt2weyk],
    event: 'reload',
  });
  const act_3573211234170528_script_vp4a3t6u6 = new actionTypes.ScriptAction({
    actionArgs: act_3573211234170528_script_vp4a3t6u6_getActionArgs,
  });
  const inst_evh_3573211234170528_optionSelected_c44q0cole =
    new actionTypes.EventHandlerImpl({
      action: act_3573211234170528_script_vp4a3t6u6,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_3573211234170528_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3573211234170528_optionSelected_c44q0cole],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_3twt2weyk,
    inst_evh_7315092382398562_reload,
    inst_evh_3573211234170528_optionSelected_c44q0cole,
    inst_evh_3573211234170528_optionSelected,
  };

  const evh_7315092382398562_reload_3twt2weyk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3twt2weyk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3573211234170528_optionSelected_c44q0cole = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3573211234170528_optionSelected_c44q0cole.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3573211234170528_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3573211234170528_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="company"
      :label="pgettext('Worksheet', 'Own company')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpFormSearchableSelect
        name="company"
        v-model="worksheet.company"
        bind-object
        :dataSource="b_8564662037462133_dataSource"
        prefetch
        textField="name"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="status"
      :label="pgettext('Worksheet', 'Status')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      required
    >
      <ItpFormSearchableSelect
        name="status"
        v-model="worksheet.status"
        valueField="value"
        textField="text"
        :options="statuses"
        required
        optionTemplate='<UcWorksheetStatus :value="option.value"></UcWorksheetStatus>'
        use-option-template-for-selected-option
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="number"
      :label="pgettext('Worksheet', 'Worksheet Number')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      required
    >
      <ItpFormInput
        name="number"
        v-model="worksheet.number"
        :readonly="isReadonly"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="description"
      :label="pgettext('Worksheet', 'Description')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="8"
      required
    >
      <ItpFormInput
        autofocus
        name="description"
        v-model="worksheet.description"
        :readonly="isReadonly"
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="gl_account_number"
      :label="pgettext('Worksheet', 'GL Account Number')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
    >
      <ItpFormInput
        name="gl_account_number"
        v-model="worksheet.gl_account_number"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      name="worksheet_date"
      :label="pgettext('Worksheet', 'Worksheet Date')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      required
    >
      <ItpFormDatetime
        name="worksheet_date"
        v-model="worksheet.worksheet_date"
        :readonly="isReadonly"
        required
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="period"
      :label="pgettext('Worksheet', 'Period')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="4"
      contentClass="row"
    >
      <ItpFormDatetime
        name="start_date"
        v-model="worksheet.start_date"
        :placeholder="pgettext('Worksheet/StartDate/placeholder', 'Start')"
        :readonly="isReadonly"
        class="col-6"
      >
      </ItpFormDatetime>
      <ItpFormDatetime
        name="end_date"
        v-model="worksheet.end_date"
        :placeholder="pgettext('Worksheet/EndDate/placeholder', 'End')"
        :readonly="isReadonly"
        class="col-6"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      name="rental"
      :label="pgettext('Worksheet', 'Rental')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpFormSearchableSelect
        name="rental"
        v-model="worksheet.rental"
        :dataSource="b_3573211234170528_dataSource"
        valueField="id"
        textField="name"
        bind-object
        :readonly="isReadonly"
        use-option-template-for-selected-option
        optionTemplate='<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>'
        @optionSelected="evh_3573211234170528_optionSelected($event, {})"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="client"
      :label="pgettext('Worksheet', 'Partner')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpFormSearchableSelect
        name="client"
        v-model="worksheet.client"
        bind-object
        :dataSource="b_6639903447389385_dataSource"
        textField="name"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="contractor"
      :label="pgettext('Worksheet', 'Contractor')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="5"
    >
      <ItpFormSearchableSelect
        name="contractor"
        v-model="worksheet.contractor"
        bind-object
        :dataSource="b_8017867030266407_dataSource"
        textField="name"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="notes"
      :label="pgettext('Worksheet', 'Notes')"
      :labelAlign="labelAlign"
      :label-cols-md="4"
      :content-cols-md="8"
    >
      <ItpFormTextarea
        name="notes"
        v-model="worksheet.notes"
        :readonly="isReadonly"
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
